import type { ReactNode } from 'react'

import { cx } from '../lib/cx'
import { HealthDot } from './HealthDot'
import { Icon, ICONO_SEVERIDAD } from './Icon'
import { SEVERIDAD_TEXTO, SEVERIDAD_TONO, type Severidad } from './tokens'

/**
 * Barra de triage: el estado general de la empresa y los conteos por severidad,
 * arriba de todo. Es la tesis del tablero de riesgo hecha componente — el panel
 * se organiza por urgencia y lo primero que se ve es cuántos frentes están
 * rotos, no qué tipo de dato hay.
 *
 * A la derecha van los botones de nivel pantalla del briefing: "Atacar el cuello
 * de botella (17 clientes)", "Cargar los datos que faltan (4)". Son acciones de
 * la pantalla completa, no de una sección.
 */
export interface TriageConteo {
  severidad: Severidad
  cantidad: number
  /** Qué se cuenta, en plural, ej. "frentes rotos". */
  label: string
  /** Ancla o ruta a donde se resuelve. Sin esto el conteo no es clickeable. */
  onClick?: () => void
}

export interface TriageBarProps {
  /** Estado general: el peor de los frentes. */
  estado: Severidad
  /** Veredicto en palabras, ej. "2 frentes rotos". Nunca solo el color. */
  titular: string
  conteos: readonly TriageConteo[]
  /** Botones de nivel pantalla. */
  acciones?: ReactNode
  /** Marca temporal a la derecha, ej. "datos al 26/07 06:00". */
  meta?: string
  className?: string
}

export function TriageBar({
  estado,
  titular,
  conteos,
  acciones,
  meta,
  className,
}: TriageBarProps) {
  return (
    <div
      role="status"
      data-testid="triage-bar"
      data-estado={estado}
      className={cx(
        'flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-border bg-surface px-5 py-3',
        className,
      )}
    >
      <span
        data-testid="triage-bar-estado"
        className={cx(
          'inline-flex items-center gap-2 rounded-md border px-2.5 py-1 text-base font-bold',
          SEVERIDAD_TONO[estado],
          SEVERIDAD_TEXTO[estado],
        )}
      >
        <Icon name={ICONO_SEVERIDAD[estado]} size={14} />
        {titular}
      </span>

      <ul className="flex list-none flex-wrap gap-x-4 gap-y-1 p-0 text-meta text-muted-strong">
        {conteos.map((conteo) => (
          <li key={conteo.label} className="flex items-center gap-1.5">
            {conteo.onClick !== undefined ? (
              <button
                type="button"
                onClick={conteo.onClick}
                className="inline-flex items-center gap-1.5 rounded px-1 py-0.5 transition-colors duration-150 ease-brand hover:bg-n100 hover:text-text"
              >
                <HealthDot
                  health={conteo.severidad}
                  size="sm"
                  showLabel={false}
                />
                <b className="tabular font-bold text-text">{conteo.cantidad}</b>
                {conteo.label}
              </button>
            ) : (
              <>
                <HealthDot
                  health={conteo.severidad}
                  size="sm"
                  showLabel={false}
                />
                <b className="tabular font-bold text-text">{conteo.cantidad}</b>
                {conteo.label}
              </>
            )}
          </li>
        ))}
      </ul>

      {acciones !== undefined ? (
        <div className="flex flex-wrap items-center gap-2">{acciones}</div>
      ) : null}

      {meta !== undefined ? (
        <span className="tabular ml-auto text-micro text-muted-strong">
          {meta}
        </span>
      ) : null}
    </div>
  )
}
